import type { ECharts, EChartsOption, SetOptionOpts } from "echarts";
import { getInstanceByDom, init } from "echarts";
import type { CSSProperties } from "react";
import { useEffect, useRef } from "react";

export type ReactEChartEvents = "legendselectchanged";

export type EventParams = {
  name: string;
  selected: Record<string, boolean>;
};

export interface ReactEChartsProps {
  option: EChartsOption;
  style?: CSSProperties;
  settings?: SetOptionOpts;
  loading?: boolean;
  theme?: "light" | "dark";
}

export function ReactECharts({
  option,
  style,
  settings,
  loading,
  theme,
}: ReactEChartsProps): JSX.Element {
  const chartRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Initialize chart
    let chart: ECharts | undefined;
    if (chartRef.current !== null) {
      chart = init(chartRef.current, theme, { renderer: "svg" });

      chart.on("legendselectchanged", (params: unknown) => {
        const { name, selected } = params as EventParams;
        const filteredSelected = Object.fromEntries(
          Object.entries(selected).filter(([key]) => key !== name)
        );
        const focusedSeriesSelected =
          !selected[name] &&
          Object.values(filteredSelected).every((value) => value);
        const selectAll =
          !selected[name] &&
          Object.values(filteredSelected).every((value) => !value);

        // If all were selected, then deselect all but the one that was clicked
        if (focusedSeriesSelected) {
          const newSelection: Record<string, boolean> = {
            [name]: true,
          };

          Object.keys(filteredSelected).forEach((key) => {
            newSelection[key] = false;
          });

          chart?.setOption({
            legend: {
              selected: newSelection,
            },
          });
        } else if (selectAll) {
          // If none were selected, then select all
          const newSelection: Record<string, boolean> = {};

          Object.keys(selected).forEach((key) => {
            newSelection[key] = true;
          });

          chart?.setOption({
            legend: {
              selected: newSelection,
            },
          });
        }
      });
    }

    // Add chart resize listener
    // ResizeObserver is leading to a bit janky UX
    function resizeChart() {
      chart?.resize();
    }
    window.addEventListener("resize", resizeChart);

    // Return cleanup function
    return () => {
      chart?.dispose();
      window.removeEventListener("resize", resizeChart);
    };
  }, [theme]);

  useEffect(() => {
    // Update chart
    if (chartRef.current !== null) {
      const chart = getInstanceByDom(chartRef.current);

      if (chart) {
        chart.setOption(option, settings);
      }
    }
  }, [option, settings, theme]); // Whenever theme changes we need to add option and setting due to it being deleted in cleanup function

  useEffect(() => {
    // Update chart
    if (chartRef.current !== null) {
      const chart = getInstanceByDom(chartRef.current);
      if (chart) {
        loading === true ? chart.showLoading() : chart.hideLoading();
      }
    }
  }, [loading, theme]);

  return (
    <div
      className="echarts-container"
      ref={chartRef}
      style={{ width: "100%", height: "100%", ...style }}
    />
  );
}
